import { UserManager } from 'oidc-client-ts';
import type { StateStore } from 'oidc-client-ts';

// #544: Keycloak is reverse-proxied behind this same origin (see vite.config.ts's /idp entry and
// compose.yml's KC_HTTP_RELATIVE_PATH) specifically so this is same-origin, not a separate host or
// port -- a different port is still a different origin to the browser, which is what made
// automaticSilentRenew's hidden-iframe request to Keycloak cross-site (and therefore unable to
// carry Keycloak's session cookie) before this fix. VITE_KEYCLOAK_AUTHORITY is now just the realm
// path under this origin, not a full external URL.
const AUTHORITY_PATH = import.meta.env.VITE_KEYCLOAK_AUTHORITY?.trim() || '/idp/realms/chem-draw';
const AUTHORITY = `${window.location.origin}${AUTHORITY_PATH}`;
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID?.trim() || 'chem-draw-spa';

// #531: a bearer token in localStorage is directly readable by any script on this origin -- if an
// XSS bug is ever introduced here, that turns it into full, reusable session theft instead of
// exposure confined to the live tab. An in-memory store (backed by a plain Map, never written to
// any storage API) confines that blast radius: nothing token-related survives a reload. #544 made
// this viable -- a reload's lost session is silently restored via signinSilent() against
// Keycloak's now-same-origin SSO cookie (see useAuth.ts's startup effect and hadPriorSession()
// below), which previously failed unconditionally regardless of any cookie the browser held.
class InMemoryStateStore implements StateStore {
  private readonly map = new Map<string, string>();

  async set(key: string, value: string): Promise<void> {
    this.map.set(key, value);
  }

  async get(key: string): Promise<string | null> {
    return this.map.get(key) ?? null;
  }

  async remove(key: string): Promise<string | null> {
    const value = this.map.get(key) ?? null;
    this.map.delete(key);
    return value;
  }

  async getAllKeys(): Promise<string[]> {
    return [...this.map.keys()];
  }

  // Synchronous escape hatch for getAccessToken() below -- oidc-client-ts's own StateStore
  // interface is async-only (modeled on WebStorageStateStore wrapping a real storage API), but
  // apiClient.ts's apiFetch() needs a token on every call without awaiting userManager.getUser().
  readSync(key: string): string | undefined {
    return this.map.get(key);
  }
}

const userStore = new InMemoryStateStore();

export const userManager = new UserManager({
  authority: AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid',
  userStore,
  automaticSilentRenew: true,
  monitorSession: true
});

// UserManager's own internal storage key -- see oidc-client-ts's UserManager._userStoreKey. No
// "oidc." prefix here: that was WebStorageStateStore's own cosmetic namespacing for a shared
// browser storage API, irrelevant to a Map only this module ever touches.
const storageKey = `user:${AUTHORITY}:${CLIENT_ID}`;

// #531: a tiny, non-sensitive marker recording only "a session existed here before" -- never a
// token, never anything usable to authenticate as anyone. Read by useAuth.ts's startup effect to
// decide whether attempting a silent restore is worth the round trip (or the full timeout against
// an unreachable authority) for a browser that never signed in, or explicitly signed out.
const PRIOR_SESSION_KEY = 'peruselab.auth.hadSession';

export function hadPriorSession(): boolean {
  return window.localStorage.getItem(PRIOR_SESSION_KEY) === 'true';
}

export function markSessionPresence(present: boolean): void {
  if (present) window.localStorage.setItem(PRIOR_SESSION_KEY, 'true');
  else window.localStorage.removeItem(PRIOR_SESSION_KEY);
}

// #531: with no more pre-boot browser storage location, tests/e2e/support/authFixture.ts's
// seeded-session pattern has nowhere left to write to before this module's own in-memory Map
// exists. Vite's page.addInitScript runs before any page script, so a value placed here is ready
// by the time this module evaluates. import.meta.env.DEV keeps this out of a production bundle
// entirely (dead code eliminated at build time) -- it only ever runs against the dev server e2e's
// own webServer starts.
if (import.meta.env.DEV) {
  const seed = (window as unknown as { __e2eAuthSeed__?: unknown }).__e2eAuthSeed__;
  if (seed) {
    void userStore.set(storageKey, JSON.stringify(seed));
    markSessionPresence(true);
  }
}

/**
 * #885: where to land after signing in -- the page a signed-out visitor asked to sign in from
 * (carried through Keycloak as the sign-in request's state). Only a same-origin path is honoured,
 * never an absolute or protocol-relative URL, so the state can't become an open redirect.
 */
export function safeReturnPath(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/')) return '/';
  if (
    [...value].some(
      (character) => character === '\\' || character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127
    )
  )
    return '/';
  try {
    const target = new URL(value, window.location.origin);
    if (target.origin !== window.location.origin || decodeURIComponent(target.pathname).startsWith('/auth/callback'))
      return '/';
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return '/';
  }
}

/**
 * #889 D5: recovery for a lost authenticator. Sign-in has no password, so Keycloak's own
 * "forgot password" link never shows on its email step; the app links to the credential-reset
 * flow directly (email link -> the old authenticator is removed -> set up a new one).
 */
export function authenticatorRecoveryUrl(): string {
  return `${AUTHORITY}/login-actions/reset-credentials?${new URLSearchParams({ client_id: CLIENT_ID })}`;
}

/** #1051: the identity provider's account page, where a user verifies their email address. */
export function accountSettingsUrl(): string {
  return `${AUTHORITY}/account`;
}

export function getAccessToken(): string | null {
  const raw = userStore.readSync(storageKey);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as { access_token?: unknown };
    return typeof stored.access_token === 'string' ? stored.access_token : null;
  } catch {
    return null;
  }
}

// #1041: cooldown tracking to break redirect loops when the server rejects newly minted tokens.
const REAUTH_TIMESTAMP_KEY = 'peruselab.auth.lastReauth';
const REAUTH_COOLDOWN_MS = 10000;

export function recordReauthTimestamp(): void {
  try {
    window.sessionStorage.setItem(REAUTH_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // SessionStorage may be restricted in some contexts
  }
}

export function clearReauthTimestamp(): void {
  try {
    window.sessionStorage.removeItem(REAUTH_TIMESTAMP_KEY);
  } catch {
    // SessionStorage may be restricted in some contexts
  }
}

// Several API requests can fail together. Keep one interactive redirect in flight.
let reauthenticationPending = false;
export async function reauthenticate(returnTo: string): Promise<void> {
  if (reauthenticationPending) return;

  let lastReauth = 0;
  try {
    lastReauth = Number(window.sessionStorage.getItem(REAUTH_TIMESTAMP_KEY) || '0');
  } catch {
    // SessionStorage may be restricted in some contexts
  }

  const now = Date.now();
  if (now - lastReauth < REAUTH_COOLDOWN_MS) {
    // #1041: Repeated 401 right after a recent sign-in / reauthentication callback:
    // the server is actively rejecting the token (e.g. audience mismatch or revoked session).
    // Abort the redirect loop to prevent continuous browser flickering.
    console.error('[auth] Repeated 401 after recent reauthentication; aborting redirect loop.');
    clearReauthTimestamp();
    markSessionPresence(false);
    await userManager.removeUser().catch(() => {});
    return;
  }

  reauthenticationPending = true;
  recordReauthTimestamp();
  try {
    await userManager.signinRedirect({ state: { returnTo: safeReturnPath(returnTo) } });
  } catch (error) {
    clearReauthTimestamp();
    throw error;
  } finally {
    reauthenticationPending = false;
  }
}

export function _resetReauthenticationState(): void {
  reauthenticationPending = false;
  clearReauthTimestamp();
}
